<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu pageTitle="Estado del Proyecto" />

    <div class="flex-grow-1 overflow-auto px-3 pb-5">

      <!-- Intro -->
      <div class="alert alert-info mb-3 small">
        <strong>Documento vivo.</strong> Refleja el estado actual del sistema web vs el sistema Windows del ingeniero.
        Actualizar cada vez que se complete un módulo, se descubra un bug o cambie una decisión técnica.<br>
        <span class="text-muted">
          Última actualización: <strong>{{ ultimaActualizacion }}</strong>
          &nbsp;·&nbsp; <em>{{ ultimoCambio }}</em>
        </span>
      </div>

      <!-- Resumen general -->
      <div class="row g-3 mb-4">
        <div class="col-6 col-md-3" v-for="s in resumen" :key="s.label">
          <div class="card text-center py-3">
            <div class="fs-3 fw-bold" :class="s.color">{{ s.valor }}</div>
            <div class="small text-muted">{{ s.label }}</div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item" v-for="tab in tabs" :key="tab.id">
          <button class="nav-link" :class="tabActivo === tab.id ? 'active' : ''" @click="tabActivo = tab.id">
            {{ tab.label }}
          </button>
        </li>
      </ul>

      <!-- TAB: Módulos -->
      <template v-if="tabActivo === 'modulos'">
        <div v-for="mod in modulos" :key="mod.nombre" class="card mb-3">
          <div class="card-header d-flex justify-content-between align-items-center py-2"
            :style="`background:${headerBgEstado(mod.estado)}`">
            <div class="d-flex align-items-center gap-2">
              <span>{{ mod.icono }}</span>
              <span class="fw-bold">{{ mod.nombre }}</span>
              <span v-if="mod.beta" class="badge bg-warning text-dark" style="font-size:10px">Beta</span>
              <code class="text-muted small">{{ mod.ruta }}</code>
            </div>
            <span class="badge" :class="estadoBadge(mod.estado)">{{ mod.estado }}</span>
          </div>
          <div class="card-body p-0">
            <div class="row g-0">

              <!-- Descripción -->
              <div class="col-md-3 p-3 border-end">
                <div class="small text-muted mb-1 fw-semibold">Qué hace</div>
                <div class="small">{{ mod.descripcion }}</div>
                <div v-if="mod.api" class="mt-2">
                  <div class="small text-muted fw-semibold mb-1">API</div>
                  <code class="small text-muted">{{ mod.api }}</code>
                </div>
              </div>

              <!-- Bugs -->
              <div class="col-md-3 p-3 border-end">
                <div class="small fw-semibold text-danger mb-2">🐛 Bugs conocidos</div>
                <div v-if="!mod.bugs.length" class="small text-muted">Sin bugs conocidos</div>
                <ul v-else class="mb-0 ps-3">
                  <li v-for="b in mod.bugs" :key="b" class="small mb-1">{{ b }}</li>
                </ul>
              </div>

              <!-- Pendientes -->
              <div class="col-md-3 p-3 border-end">
                <div class="small fw-semibold text-warning mb-2">⏳ Pendientes</div>
                <div v-if="!mod.pendientes.length" class="small text-muted">Sin pendientes</div>
                <ul v-else class="mb-0 ps-3">
                  <li v-for="p in mod.pendientes" :key="p" class="small mb-1"
                    :class="p.startsWith('🔴') ? 'text-danger' : p.startsWith('🟡') ? 'text-warning' : ''">
                    {{ p }}
                  </li>
                </ul>
              </div>

              <!-- Diferencia con Windows -->
              <div class="col-md-3 p-3">
                <div class="small fw-semibold text-primary mb-2">🖥 vs Sistema Windows</div>
                <div v-if="!mod.vsWindows" class="small text-muted">Sin diferencias documentadas</div>
                <div v-else class="small">{{ mod.vsWindows }}</div>
              </div>

            </div>
          </div>
        </div>
      </template>

      <!-- TAB: Windows vs Web -->
      <template v-if="tabActivo === 'comparativa'">
        <div class="card mb-3" v-for="comp in comparativa" :key="comp.modulo">
          <div class="card-header py-2 fw-bold">{{ comp.icono }} {{ comp.modulo }}</div>
          <div class="card-body p-0">
            <div class="row g-0">
              <div class="col-md-6 p-3 border-end" style="background:#fff8f0">
                <div class="small fw-semibold text-warning mb-2">🖥 Sistema Windows (ingeniero)</div>
                <ul class="small mb-0 ps-3">
                  <li v-for="p in comp.windows" :key="p" class="mb-1">{{ p }}</li>
                </ul>
              </div>
              <div class="col-md-6 p-3" style="background:#f0fff4">
                <div class="small fw-semibold text-success mb-2">🌐 Sistema Web (nuevo)</div>
                <ul class="small mb-0 ps-3">
                  <li v-for="p in comp.web" :key="p" class="mb-1">{{ p }}</li>
                </ul>
              </div>
            </div>
            <div v-if="comp.decision" class="px-3 py-2 border-top bg-light">
              <span class="small text-muted"><strong>Decisión:</strong> {{ comp.decision }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- TAB: Arquitectura BD -->
      <template v-if="tabActivo === 'bd'">
        <div class="card mb-3" v-for="sec in seccionesBd" :key="sec.titulo">
          <div class="card-header py-2 fw-bold">{{ sec.titulo }}</div>
          <div class="card-body">
            <div v-for="item in sec.items" :key="item.nombre" class="mb-2">
              <code class="fw-bold small">{{ item.nombre }}</code>
              <span class="small text-muted ms-2">{{ item.desc }}</span>
              <span v-if="item.nota" class="badge bg-warning text-dark ms-2" style="font-size:10px">{{ item.nota }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- TAB: MER -->
      <template v-if="tabActivo === 'mer'">
        <div class="alert alert-info small mb-3">
          Diagrama de entidad-relación con las tablas que SportXXI <strong>realmente usa</strong>.
          Flechas = FK reales. Colores por dominio. Basado en análisis junio 2026 + diagrama de Oscar.
        </div>

        <div class="card mb-3">
          <div class="card-header py-2 fw-bold d-flex justify-content-between align-items-center">
            <span>🗺 MER SportXXI — tablas activas</span>
            <span class="small text-muted">
              <span style="display:inline-block;width:10px;height:10px;background:#1d4ed8;border-radius:2px;"></span> Personas &nbsp;
              <span style="display:inline-block;width:10px;height:10px;background:#15803d;border-radius:2px;"></span> Productos &nbsp;
              <span style="display:inline-block;width:10px;height:10px;background:#c2410c;border-radius:2px;"></span> Compras &nbsp;
              <span style="display:inline-block;width:10px;height:10px;background:#86198f;border-radius:2px;"></span> Ventas &nbsp;
              <span style="display:inline-block;width:10px;height:10px;background:#5b21b6;border-radius:2px;"></span> Transferencias &nbsp;
              <span style="display:inline-block;width:10px;height:10px;background:#854d0e;border-radius:2px;"></span> Kardex &nbsp;
              <span style="display:inline-block;width:10px;height:10px;background:#475569;border-radius:2px;"></span> Config
            </span>
          </div>
          <div class="card-body p-0" style="overflow-x:auto;">
          <div style="min-width:1040px;">
            <!-- SVG MER con posiciones fijas y flechas de routing limpio -->
            <svg viewBox="0 0 1040 590" style="width:100%; min-width:900px; display:block;"
                 font-family="ui-monospace, 'Cascadia Code', monospace" font-size="9">
              <defs>
                <marker id="arr" markerWidth="7" markerHeight="6" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L7,3 L0,6 Z" fill="context-stroke"/>
                </marker>
              </defs>

              <!-- ═══════════════════════════════════════════════════ -->
              <!-- FLECHAS (dibujadas primero, debajo de las tablas)  -->
              <!-- ═══════════════════════════════════════════════════ -->

              <!-- tbpresentacion → tbproducto (vertical, 1:1) -->
              <path d="M280,195 L280,173" stroke="#15803d" stroke-width="1.5" fill="none" marker-end="url(#arr)"/>
              <text x="285" y="186" font-size="8" fill="#15803d">1:1</text>

              <!-- tbregistro_doc_ingreso → tbpersona (proveedor) -->
              <path d="M400,130 C360,130 300,150 165,155" stroke="#1d4ed8" stroke-width="1.5" fill="none" stroke-dasharray="4,2" marker-end="url(#arr)"/>
              <text x="290" y="128" font-size="8" fill="#1d4ed8">proveedor</text>

              <!-- tbregistro_doc_ingreso → tbalmacen (sede) -->
              <path d="M487,110 C487,85 502,78 502,75" stroke="#475569" stroke-width="1.3" fill="none" stroke-dasharray="3,2" marker-end="url(#arr)"/>
              <text x="510" y="95" font-size="8" fill="#475569">sede</text>

              <!-- tbdetalle_reg_ing → tbregistro_doc_ingreso (cabecera, vertical) -->
              <path d="M475,252 L475,218" stroke="#c2410c" stroke-width="1.5" fill="none" marker-end="url(#arr)"/>
              <text x="480" y="238" font-size="8" fill="#c2410c">N:1</text>

              <!-- tbdetalle_reg_ing → tbpresentacion (talla comprada) -->
              <path d="M400,270 C385,270 375,235 360,230" stroke="#15803d" stroke-width="1.3" fill="none" stroke-dasharray="4,2" marker-end="url(#arr)"/>
              <text x="370" y="260" font-size="8" fill="#15803d">talla</text>

              <!-- tbregistro_doc_pedido → tbpersona (cliente) -->
              <path d="M600,145 C520,145 380,162 165,168" stroke="#1d4ed8" stroke-width="1.5" fill="none" stroke-dasharray="4,2" marker-end="url(#arr)"/>
              <text x="370" y="143" font-size="8" fill="#1d4ed8">cliente</text>

              <!-- tbregistro_doc_pedido → tbusuario (vendedor) -->
              <path d="M655,110 C655,80 672,75 672,72" stroke="#475569" stroke-width="1.3" fill="none" stroke-dasharray="3,2" marker-end="url(#arr)"/>
              <text x="660" y="93" font-size="8" fill="#475569">vendedor</text>

              <!-- tbregistro_doc_pedido → tbalmacen (sede ventas) -->
              <path d="M715,110 C715,75 600,48 575,45" stroke="#475569" stroke-width="1.3" fill="none" stroke-dasharray="3,2" marker-end="url(#arr)"/>

              <!-- tbdetalle_reg_ped → tbregistro_doc_pedido (vertical) -->
              <path d="M694,267 L694,234" stroke="#86198f" stroke-width="1.5" fill="none" marker-end="url(#arr)"/>
              <text x="698" y="254" font-size="8" fill="#86198f">N:1</text>

              <!-- tbdetalle_reg_ped → tbpresentacion (talla vendida) -->
              <path d="M600,285 C560,285 430,245 360,240" stroke="#15803d" stroke-width="1.3" fill="none" stroke-dasharray="4,2" marker-end="url(#arr)"/>
              <text x="468" y="278" font-size="8" fill="#15803d">talla</text>

              <!-- tbctas_cobrar → tbregistro_doc_pedido (vertical, offseteado) -->
              <path d="M677,360 L677,234" stroke="#86198f" stroke-width="1.3" fill="none" stroke-dasharray="3,2" marker-end="url(#arr)"/>
              <text x="648" y="305" font-size="8" fill="#86198f">cobro</text>

              <!-- tbusuario → tbpersona (largo) -->
              <path d="M605,50 C490,50 350,148 165,155" stroke="#1d4ed8" stroke-width="1.3" fill="none" stroke-dasharray="3,2" marker-end="url(#arr)"/>
              <text x="380" y="44" font-size="8" fill="#1d4ed8">es persona</text>

              <!-- tbregistrotransferencia_salida → tbalmacen -->
              <path d="M820,128 C750,128 650,50 575,44" stroke="#5b21b6" stroke-width="1.3" fill="none" stroke-dasharray="4,2" marker-end="url(#arr)"/>
              <text x="690" y="122" font-size="8" fill="#5b21b6">sede</text>

              <!-- tbdetalletransferencia_salida → tbregistrotransferencia_salida (vertical) -->
              <path d="M907,228 L907,200" stroke="#5b21b6" stroke-width="1.5" fill="none" marker-end="url(#arr)"/>
              <text x="912" y="217" font-size="8" fill="#5b21b6">N:1</text>

              <!-- tbdetalletransferencia_salida → tbpresentacion (horizontal largo) -->
              <path d="M820,252 C750,252 440,248 360,245" stroke="#15803d" stroke-width="1.3" fill="none" stroke-dasharray="4,2" marker-end="url(#arr)"/>
              <text x="570" y="246" font-size="8" fill="#15803d">talla</text>

              <!-- tbinventarioparcialdetallesprod → tbinventarioparcialcab -->
              <path d="M390,513 L355,510" stroke="#854d0e" stroke-width="1.5" fill="none" marker-end="url(#arr)"/>
              <text x="356" y="505" font-size="8" fill="#854d0e">N:1</text>

              <!-- tbinventarioparcialdetallesprod → tbpresentacion (subiendo) -->
              <path d="M472,490 C472,400 320,290 280,262" stroke="#15803d" stroke-width="1.3" fill="none" stroke-dasharray="4,2" marker-end="url(#arr)"/>
              <text x="410" y="395" font-size="8" fill="#15803d">talla</text>

              <!-- ═══════════════ -->
              <!-- TABLAS (encima) -->
              <!-- ═══════════════ -->

              <!-- ── tbpersona ── -->
              <g transform="translate(10,110)">
                <rect width="155" height="104" rx="4" fill="white" stroke="#93c5fd" stroke-width="1.5"/>
                <rect width="155" height="20" rx="3" fill="#1d4ed8"/>
                <rect y="17" width="155" height="3" fill="#1d4ed8"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9.5">tbpersona</text>
                <text x="149" y="14" text-anchor="end" fill="#93c5fd" font-size="8">~45k</text>
                <text x="6" y="32" fill="#92400e">PK idpersona</text>
                <text x="6" y="46" fill="#374151">nombre / razonsoc</text>
                <text x="6" y="60" fill="#374151">ruc / dni</text>
                <text x="6" y="74" fill="#374151">tipopers (P/E/N)</text>
                <text x="6" y="88" fill="#9ca3af" font-style="italic">+ tblocales / tbcontacto</text>
              </g>

              <!-- ── tbproducto ── -->
              <g transform="translate(200,75)">
                <rect width="160" height="98" rx="4" fill="white" stroke="#86efac" stroke-width="2"/>
                <rect width="160" height="20" rx="3" fill="#15803d"/>
                <rect y="17" width="160" height="3" fill="#15803d"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9.5">tbproducto</text>
                <text x="154" y="14" text-anchor="end" fill="#86efac" font-size="8">~145k</text>
                <text x="6" y="32" fill="#6b7280" font-style="italic">1 fila = 1 talla</text>
                <text x="6" y="46" fill="#92400e">PK idprod</text>
                <text x="6" y="60" fill="#4338ca">FK idlinea/idmarca/idtalla</text>
                <text x="6" y="74" fill="#374151">nombre / codigo_prov / usa</text>
              </g>

              <!-- ── tbpresentacion ── -->
              <g transform="translate(200,195)">
                <rect width="160" height="68" rx="4" fill="white" stroke="#86efac" stroke-width="1.5"/>
                <rect width="160" height="20" rx="3" fill="#15803d"/>
                <rect y="17" width="160" height="3" fill="#15803d"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9.5">tbpresentacion</text>
                <text x="154" y="14" text-anchor="end" fill="#86efac" font-size="8">~145k</text>
                <text x="6" y="32" fill="#92400e">PK idpres</text>
                <text x="6" y="46" fill="#4338ca">FK idprod</text>
                <text x="6" y="60" fill="#374151">codbarras / precvta</text>
              </g>

              <!-- ── tbalmacen ── -->
              <g transform="translate(430,10)">
                <rect width="145" height="68" rx="4" fill="white" stroke="#cbd5e1" stroke-width="1.5"/>
                <rect width="145" height="20" rx="3" fill="#475569"/>
                <rect y="17" width="145" height="3" fill="#475569"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9.5">tbalmacen</text>
                <text x="6" y="32" fill="#92400e">PK idalmacen</text>
                <text x="6" y="46" fill="#374151">nombre / idlocal</text>
                <text x="6" y="60" fill="#9ca3af" font-style="italic">sedes 939, 1179, 11155</text>
              </g>

              <!-- ── tbusuario ── -->
              <g transform="translate(610,10)">
                <rect width="155" height="82" rx="4" fill="white" stroke="#cbd5e1" stroke-width="1.5"/>
                <rect width="155" height="20" rx="3" fill="#475569"/>
                <rect y="17" width="155" height="3" fill="#475569"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9.5">tbusuario</text>
                <text x="6" y="32" fill="#92400e">PK idusuario</text>
                <text x="6" y="46" fill="#4338ca">FK idpersona</text>
                <text x="6" y="60" fill="#374151">usuario / clave</text>
                <text x="6" y="74" fill="#374151">nivelacceso (1/2/3)</text>
              </g>

              <!-- ── tbregistro_doc_ingreso ── -->
              <g transform="translate(400,110)">
                <rect width="172" height="108" rx="4" fill="white" stroke="#fdba74" stroke-width="1.5"/>
                <rect width="172" height="20" rx="3" fill="#c2410c"/>
                <rect y="17" width="172" height="3" fill="#c2410c"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9">tbregistro_doc_ingreso</text>
                <text x="166" y="14" text-anchor="end" fill="#fdba74" font-size="8">2.6k</text>
                <text x="6" y="32" fill="#92400e">PK id</text>
                <text x="6" y="46" fill="#4338ca">FK idpersona (proveedor)</text>
                <text x="6" y="60" fill="#4338ca">FK idalmacen</text>
                <text x="6" y="74" fill="#374151">serie / numero / tipodoc</text>
                <text x="6" y="88" fill="#374151">fecha_recep / total</text>
                <text x="6" y="102" fill="#c2410c" font-weight="600">actualizastock (bool) ★</text>
              </g>

              <!-- ── tbdetalle_reg_ing ── -->
              <g transform="translate(400,250)">
                <rect width="172" height="68" rx="4" fill="white" stroke="#fdba74" stroke-width="1.5"/>
                <rect width="172" height="20" rx="3" fill="#c2410c"/>
                <rect y="17" width="172" height="3" fill="#c2410c"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9">tbdetalle_reg_ing</text>
                <text x="166" y="14" text-anchor="end" fill="#fdba74" font-size="8">97k</text>
                <text x="6" y="32" fill="#4338ca">FK id → doc_ingreso</text>
                <text x="6" y="46" fill="#4338ca">FK idpres</text>
                <text x="6" y="60" fill="#374151">cantidad / costo / moneda</text>
              </g>

              <!-- ── tbregistro_doc_pedido ── -->
              <g transform="translate(600,110)">
                <rect width="175" height="124" rx="4" fill="white" stroke="#f0abfc" stroke-width="2"/>
                <rect width="175" height="20" rx="3" fill="#86198f"/>
                <rect y="17" width="175" height="3" fill="#86198f"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9">tbregistro_doc_pedido</text>
                <text x="169" y="14" text-anchor="end" fill="#f0abfc" font-size="8">183k</text>
                <text x="6" y="32" fill="#92400e">PK idpedido</text>
                <text x="6" y="46" fill="#4338ca">FK idpersona (cliente)</text>
                <text x="6" y="60" fill="#4338ca">FK idusuario (vendedor)</text>
                <text x="6" y="74" fill="#4338ca">FK idalmacen</text>
                <text x="6" y="88" fill="#374151">nrodoc / tipodoc / serie</text>
                <text x="6" y="102" fill="#374151">fecha / total / igv</text>
                <text x="6" y="116" fill="#374151">estado (bool activa)</text>
              </g>

              <!-- ── tbdetalle_reg_ped ── -->
              <g transform="translate(600,267)">
                <rect width="175" height="68" rx="4" fill="white" stroke="#f0abfc" stroke-width="1.5"/>
                <rect width="175" height="20" rx="3" fill="#86198f"/>
                <rect y="17" width="175" height="3" fill="#86198f"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9">tbdetalle_reg_ped</text>
                <text x="169" y="14" text-anchor="end" fill="#f0abfc" font-size="8">~600k</text>
                <text x="6" y="32" fill="#4338ca">FK idpedido</text>
                <text x="6" y="46" fill="#4338ca">FK idpres</text>
                <text x="6" y="60" fill="#374151">cantidad / precio / descuento</text>
              </g>

              <!-- ── tbctas_cobrar ── -->
              <g transform="translate(600,360)">
                <rect width="175" height="68" rx="4" fill="white" stroke="#f0abfc" stroke-width="1.5"/>
                <rect width="175" height="20" rx="3" fill="#86198f"/>
                <rect y="17" width="175" height="3" fill="#86198f"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="9">tbctas_cobrar</text>
                <text x="169" y="14" text-anchor="end" fill="#f0abfc" font-size="8">~183k</text>
                <text x="6" y="32" fill="#4338ca">FK idpedido</text>
                <text x="6" y="46" fill="#374151">idvendedor / monto</text>
                <text x="6" y="60" fill="#374151">pagado / fecha</text>
              </g>

              <!-- ── tbregistrotransferencia_salida ── -->
              <g transform="translate(820,100)">
                <rect width="180" height="100" rx="4" fill="white" stroke="#c4b5fd" stroke-width="1.5"/>
                <rect width="180" height="20" rx="3" fill="#5b21b6"/>
                <rect y="17" width="180" height="3" fill="#5b21b6"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="8.5">tbregistrotransf_salida</text>
                <text x="174" y="14" text-anchor="end" fill="#c4b5fd" font-size="8">39.6k</text>
                <text x="6" y="32" fill="#92400e">PK id</text>
                <text x="6" y="46" fill="#4338ca">FK idorigen (almacen)</text>
                <text x="6" y="60" fill="#4338ca">FK iddestino (almacen)</text>
                <text x="6" y="74" fill="#374151">serie / numero / fecha</text>
                <text x="6" y="88" fill="#9ca3af" font-style="italic">939↔1179: 39k mvtos</text>
              </g>

              <!-- ── tbdetalletransferencia_salida ── -->
              <g transform="translate(820,228)">
                <rect width="180" height="68" rx="4" fill="white" stroke="#c4b5fd" stroke-width="1.5"/>
                <rect width="180" height="20" rx="3" fill="#5b21b6"/>
                <rect y="17" width="180" height="3" fill="#5b21b6"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="8.5">tbdetalletransf_salida</text>
                <text x="6" y="32" fill="#4338ca">FK id_salida</text>
                <text x="6" y="46" fill="#4338ca">FK idpres</text>
                <text x="6" y="60" fill="#374151">cantidad</text>
              </g>

              <!-- ── tbinventarioparcialcab ── -->
              <g transform="translate(200,490)">
                <rect width="155" height="55" rx="4" fill="white" stroke="#fde047" stroke-width="1.5"/>
                <rect width="155" height="20" rx="3" fill="#854d0e"/>
                <rect y="17" width="155" height="3" fill="#854d0e"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="8.5">tbinventarioparcialcab</text>
                <text x="149" y="14" text-anchor="end" fill="#fde047" font-size="8">3</text>
                <text x="6" y="32" fill="#92400e">PK id / fecha</text>
                <text x="6" y="46" fill="#9ca3af" font-style="italic">conteo físico 22/23/25</text>
              </g>

              <!-- ── tbinventarioparcialdetallesprod ── -->
              <g transform="translate(390,490)">
                <rect width="175" height="82" rx="4" fill="white" stroke="#fde047" stroke-width="1.5"/>
                <rect width="175" height="20" rx="3" fill="#854d0e"/>
                <rect y="17" width="175" height="3" fill="#854d0e"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="8">tbinventarioparcialdetallesprod</text>
                <text x="169" y="14" text-anchor="end" fill="#fde047" font-size="8">133k</text>
                <text x="6" y="32" fill="#4338ca">FK inventarioparcialcab_id</text>
                <text x="6" y="46" fill="#4338ca">FK idpres / idalmacen</text>
                <text x="6" y="60" fill="#374151">stock / stockp1 / stockp2</text>
                <text x="6" y="76" fill="#9ca3af" font-style="italic">2 personas cuentan c/producto</text>
              </g>

              <!-- ── inventarios.inventario ── -->
              <g transform="translate(10,490)">
                <rect width="160" height="55" rx="4" fill="white" stroke="#fde047" stroke-width="1.5"/>
                <rect width="160" height="20" rx="3" fill="#854d0e"/>
                <rect y="17" width="160" height="3" fill="#854d0e"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="8.5">inventarios.inventario</text>
                <text x="6" y="32" fill="#4338ca">FK idpres / idalmacen</text>
                <text x="6" y="46" fill="#374151">stock</text>
              </g>

              <!-- ── tbreporte_kardex (solo referencia) ── -->
              <g transform="translate(600,460)">
                <rect width="200" height="70" rx="4" fill="white" stroke="#fde047" stroke-width="1.5" stroke-dasharray="5,3"/>
                <rect width="200" height="20" rx="3" fill="#854d0e" opacity="0.7"/>
                <rect y="17" width="200" height="3" fill="#854d0e" opacity="0.7"/>
                <text x="6" y="14" font-weight="700" fill="white" font-size="8">tbreporte_kardex_valorizado</text>
                <text x="6" y="32" fill="#9ca3af" font-style="italic">pre-calculado por batch</text>
                <text x="6" y="46" fill="#4338ca">FK idpres / idalmacen</text>
                <text x="6" y="60" fill="#374151">fecha / tipo_mov / cantidad</text>
              </g>

              <!-- nota kardex -->
              <text x="603" y="542" font-size="8" fill="#c2410c">★ solo aparece si actualizastock=true</text>

              <!-- ── Separador de zona kardex ── -->
              <rect x="5" y="475" width="810" height="110" rx="6" fill="none" stroke="#fde047" stroke-width="1" stroke-dasharray="6,3" opacity="0.6"/>
              <text x="10" y="472" font-size="8" font-weight="700" fill="#854d0e" letter-spacing="0.05em">📊 KARDEX / INVENTARIO</text>

              <!-- ── Zona config (borde) ── -->
              <rect x="425" y="5" width="355" height="95" rx="6" fill="none" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="5,3" opacity="0.6"/>
              <text x="430" y="3" font-size="8" font-weight="700" fill="#475569" letter-spacing="0.05em">⚙️ CONFIG</text>

            </svg>

              <!-- Reglas clave -->
              <div class="alert alert-secondary small mb-0 mx-2 mb-2">
                <strong>Reglas clave:</strong>
                1 producto = N filas (1 por talla, cada talla tiene su propio <code>idprod</code> e <code>idpres</code>) ·
                <code>actualizastock=true</code> → stock y kardex actualizados ·
                <code>dblink</code> = migración anual manual, NO tiempo real ·
                BD rota cada año (<code>sportxxi20XX</code>) ·
                <code>tbfacturacionelectronica</code> = respuesta SUNAT por cada documento electrónico ·
                <code>inventarios.inventario</code> puede estar desactualizado (usa kardex como fuente de verdad)
              </div>

          </div><!-- /min-width wrapper -->
          </div><!-- /card-body overflow -->
        </div><!-- /card -->
      </template>

      <!-- TAB: Migración final -->
      <template v-if="tabActivo === 'migracion'">
        <div class="card mb-3 border-primary">
          <div class="card-header fw-bold d-flex justify-content-between" style="background:#eff6ff">
            <span>🚀 Plan de migración al servidor de la empresa</span>
            <span class="badge bg-secondary">Pendiente</span>
          </div>
          <div class="card-body">
            <ol class="small mb-3">
              <li class="mb-2">Obtener último backup del sistema del ingeniero (<code>digitalkhipuq_sportxxi2023.backup</code>)</li>
              <li class="mb-2">En servidor propio: <code>pg_restore -d sportxxi2023 backup.backup</code></li>
              <li class="mb-2">Correr <code>migrations/001_roles_usuarios.sql</code> — crea tablas del sistema nuevo</li>
              <li class="mb-2">Apuntar backend a la nueva BD (<code>.env</code>)</li>
              <li class="mb-2">El sistema web es dueño completo — el sistema Windows queda fuera</li>
            </ol>
            <div class="alert alert-warning small py-2 mb-0">
              <strong>Regla fija:</strong> NO tocar la BD de producción del ingeniero (<code>51.161.106.72</code>).
              Solo trabajamos en local (<code>sportxxi2023</code>) hasta que se haga la migración final.
            </div>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-header py-2 fw-bold">Tablas nuevas que agrega el sistema web</div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0 small">
              <thead class="table-light">
                <tr><th>Tabla</th><th>Para qué</th><th>Script</th></tr>
              </thead>
              <tbody>
                <tr><td><code>tbrefresh_tokens</code></td><td>Autenticación JWT</td><td><code>migrations/001...</code></td></tr>
                <tr><td><code>tbaccesos_api</code></td><td>Log de logins</td><td><code>migrations/001...</code></td></tr>
                <tr><td><code>tbventas_anuladas</code></td><td>Vacía, evita errores en queries</td><td><code>migrations/001...</code></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-header py-2 fw-bold">Columnas nuevas en tablas existentes</div>
          <div class="card-body p-0">
            <table class="table table-sm mb-0 small">
              <thead class="table-light">
                <tr><th>Tabla</th><th>Columna</th><th>Para qué</th></tr>
              </thead>
              <tbody>
                <tr><td><code>tbdetalle_reg_ing</code></td><td><code>cantidad_recibida</code></td><td>Módulo recepción parcial</td></tr>
                <tr><td><code>tbdetalle_reg_ing</code></td><td><code>barcode_recepcion</code></td><td>Barcode asignado al recibir</td></tr>
                <tr><td><code>tbdetalle_reg_ing</code></td><td><code>stock_actualizado</code></td><td>Flag por fila de recepción</td></tr>
                <tr><td><code>tbregistro_doc_ingreso</code></td><td><code>hora_registro</code></td><td>Timestamp con hora exacta</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import MainHeader from '../../components/Layouts/MainHeader.vue';
import MainSidebar from '../../components/Layouts/MainSidebar.vue';
import BreadcrumbMenu from '../../components/Common/BreadcrumbMenu.vue';

export default {
  name: 'EstadoProyectoPage',
  components: { MainHeader, MainSidebar, BreadcrumbMenu },
  data() {
    return {
      ultimaActualizacion: '29/06/2026 03:22',
      ultimoCambio: 'Tab MER con SVG puro — tablas con posiciones fijas, flechas FK con routing limpio por dominio',
      tabActivo: 'modulos',
      merRelaciones: [],
      merSvgW: 1400,
      merSvgH: 800,
      tabs: [
        { id: 'modulos',     label: '📋 Módulos' },
        { id: 'comparativa', label: '🖥 Windows vs Web' },
        { id: 'bd',          label: '🗄 Arquitectura BD' },
        { id: 'mer',         label: '🗺 MER' },
        { id: 'migracion',   label: '🚀 Migración final' },
      ],

      modulos: [
        {
          nombre: 'Nueva Compra (manual)',
          icono: '🧾',
          beta: false,
          estado: 'Funcional',
          ruta: '/nueva-compra',
          api: 'POST /api/compras',
          descripcion: 'Registro manual de una compra/factura de proveedor. Busca productos por barcode o código, agrega líneas, calcula totales con IGV. Actualiza stock inmediatamente (actualizastock=true). Igual al flujo del sistema Windows.',
          vsWindows: 'Replica exactamente el flujo del sistema Windows: una sola pantalla, stock se actualiza al confirmar, kardex registrado inmediatamente.',
          bugs: [],
          pendientes: [
            '🟡 Testear con una factura real completa',
            '🟡 Validar que la cuenta por pagar se crea correctamente',
          ],
        },
        {
          nombre: 'Importar XML + PDF',
          icono: '📄',
          beta: true,
          estado: 'Funcional',
          ruta: '/importar-xml',
          api: 'POST /api/compras/parsear-xml → POST /api/compras/confirmar-xml',
          descripcion: 'Sube la factura electrónica SUNAT (XML UBL 2.1) + PDF. Parsea productos, montos, IGV y tallas automáticamente desde el PDF. Crea el registro de compra con actualizastock=false (pendiente de recepción física).',
          vsWindows: 'No existe en Windows. Es un flujo adicional para cuando llega la factura electrónica antes que la mercadería física. El stock NO se actualiza aquí.',
          bugs: [],
          pendientes: [
            '🟡 Obtener % de margen del usuario para auto-calcular precio de venta',
          ],
        },
        {
          nombre: 'Recepción de Mercadería',
          icono: '📦',
          beta: true,
          estado: 'Funcional',
          ruta: '/recepcion-mercaderia',
          api: 'POST /api/compras/:id/recibir → POST /api/compras/:id/cerrar-recepcion',
          descripcion: 'Paso 2 del flujo XML. Permite recibir físicamente la mercadería de una compra importada. Asigna barcode, género/línea y precio de venta por producto. Soporta recepción parcial (varias entregas).',
          vsWindows: 'No existe en Windows. El Windows registra todo en un solo acto. Este módulo existe porque el XML llega antes que la mercadería.',
          bugs: [],
          pendientes: [
            '🟡 Auto-cálculo de precio de venta con % de margen',
          ],
        },
        {
          nombre: 'Historial de Compras',
          icono: '🗂️',
          beta: false,
          estado: 'Funcional',
          ruta: '/purchase-list',
          api: 'GET /api/compras',
          descripcion: 'Lista todas las compras registradas con filtros. Permite ver el detalle por producto/talla y anular una compra (revierte stock y cuentas por pagar).',
          vsWindows: 'Equivalente al listado de facturas del Windows. La anulación replica el SP paregistro_doc_ingreso_anular.',
          bugs: [],
          pendientes: [],
        },
        {
          nombre: 'Lista de Productos',
          icono: '👟',
          beta: false,
          estado: 'Funcional',
          ruta: '/product-list',
          api: 'GET /api/productos',
          descripcion: 'Catálogo completo con stock por sede, tallas, barcodes y precios. Vista por lista y por línea. Modal de kardex histórico. Oculta productos sin precio ni barcode (pendientes de recepción).',
          vsWindows: 'El Windows muestra todos los productos incluyendo pendientes. Nosotros los filtramos para evitar confusión.',
          bugs: [],
          pendientes: [
            '🟡 Kardex solo muestra histórico del ingeniero — productos nuevos aparecen después que el ingeniero regenere el reporte',
          ],
        },
        {
          nombre: 'POS de Ventas',
          icono: '🛒',
          beta: true,
          estado: 'Funcional',
          ruta: '/pos',
          api: 'POST /api/ventas',
          descripcion: 'Punto de venta. Busca productos por barcode o nombre, agrega al carrito, registra la venta con vendedor y forma de pago.',
          vsWindows: 'Replica el POS del Windows. Escribe en tbregistro_doc_pedido (misma tabla que usa el Windows).',
          bugs: [],
          pendientes: [
            '🟡 Testear flujo completo con productos reales',
          ],
        },
        {
          nombre: 'Historial de Ventas',
          icono: '📊',
          beta: false,
          estado: 'Funcional',
          ruta: '/sales-list',
          api: 'GET /api/ventas',
          descripcion: 'Lista de ventas con filtros por fecha, sede y vendedor. Permite ver el detalle de cada venta.',
          vsWindows: 'Lee de tbregistro_doc_pedido — misma fuente que el Windows.',
          bugs: [],
          pendientes: [],
        },
        {
          nombre: 'Proveedores',
          icono: '🏭',
          beta: false,
          estado: 'Funcional',
          ruta: '/proveedores',
          api: 'GET /api/proveedores',
          descripcion: 'Lista de proveedores con nombre, RUC e información de contacto.',
          vsWindows: 'Solo lectura por ahora. El Windows permite crear/editar proveedores.',
          bugs: [],
          pendientes: [
            '🟡 Agregar creación y edición de proveedores',
          ],
        },
        {
          nombre: 'Dashboard',
          icono: '📈',
          beta: false,
          estado: 'Básico',
          ruta: '/',
          api: 'GET /api/ventas/resumen',
          descripcion: 'Resumen de ventas recientes y métricas generales.',
          vsWindows: 'El Windows no tiene dashboard. Es una adición del sistema web.',
          bugs: [],
          pendientes: [
            '🟡 Definir métricas útiles: ventas del día, stock bajo, compras pendientes',
          ],
        },
      ],

      comparativa: [
        {
          modulo: 'Registro de Compras',
          icono: '🧾',
          windows: [
            'Pantalla manual única: proveedor, serie, número, fecha, almacén',
            'El usuario agrega productos uno a uno (barcode o búsqueda)',
            'Al confirmar: stock actualiza inmediatamente (actualizastock=true)',
            'Kardex registrado en el mismo acto',
            'Cuenta por pagar creada automáticamente',
            'Llama SP paregistro_doc_ingreso_actualizar',
          ],
          web: [
            'OPCIÓN 1 — Nueva Compra (/nueva-compra): idéntico al Windows, stock inmediato',
            'OPCIÓN 2 — Importar XML (/importar-xml): sube factura electrónica SUNAT, stock diferido',
            'Ambas opciones escriben en las mismas tablas (tbregistro_doc_ingreso + tbdetalle_reg_ing)',
            'Cuenta por pagar creada automáticamente igual que Windows',
          ],
          decision: 'Nueva Compra replica el Windows 1:1. Importar XML es un flujo adicional para facturas electrónicas que llegan antes que la mercadería.',
        },
        {
          modulo: 'Recepción de Mercadería',
          icono: '📦',
          windows: [
            'No existe como módulo separado',
            'La compra y la recepción son el mismo acto',
            'Opcional: guía de remisión previa (tbregistro_doc_guia_ingreso)',
          ],
          web: [
            'Existe solo para el flujo XML (actualizastock=false)',
            'Permite recibir parcialmente en varias sesiones',
            'Asigna barcode, género/línea y precio de venta por producto',
            '"Confirmar lo recibido" → stock entra por fila (stock_actualizado=true)',
            '"Cerrar recepción" → actualizastock=true → kardex registrado',
          ],
          decision: 'Para la Nueva Compra manual no se usa Recepción. Solo aplica al flujo XML donde la mercadería llega días después de la factura.',
        },
        {
          modulo: 'Kardex / Movimientos',
          icono: '📋',
          windows: [
            'SP generarmovimientos_inventario() hace TRUNCATE + reconstrucción desde cero',
            'Lee tbregistro_doc_ingreso WHERE actualizastock=true',
            'Escribe en inventarios.inventario → luego pareportekardex → tbreporte_kardex_valorizado_detalle',
            'Es un proceso batch que corre manualmente o por schedule',
          ],
          web: [
            'Modal de kardex lee tbreporte_kardex_valorizado_detalle (misma tabla del Windows)',
            'Para compras nuevas (Nueva Compra): kardex en tbkardexingreso inmediatamente',
            'Para compras XML: kardex después de cerrarRecepcion + regeneración del ingeniero',
            'Productos nuevos aparecen en el modal después del próximo batch del ingeniero',
          ],
          decision: 'No reinventamos el kardex. Usamos la misma fuente del Windows. Compatibilidad total.',
        },
        {
          modulo: 'Ventas / POS',
          icono: '🛒',
          windows: [
            'POS propio con pantalla de cobro, cambio, múltiples formas de pago',
            'Escribe en tbregistro_doc_pedido + tbdetalle_reg_pedido',
            'Llama SP paregistro_doc_pedido_venta_actualizar',
          ],
          web: [
            'POS web lee y escribe en las mismas tablas (tbregistro_doc_pedido)',
            'Ambos sistemas pueden coexistir escribiendo en la misma BD',
            'Vendedor obligatorio, validado contra tbpersona',
          ],
          decision: 'Coexistencia total. El Windows y el web pueden registrar ventas simultáneamente en la misma BD.',
        },
        {
          modulo: 'Autenticación',
          icono: '🔐',
          windows: [
            'Login propio con tabla de usuarios en tbpersona (campo usuario + clave)',
            'Sin JWT, sin tokens — sesión local de Windows',
          ],
          web: [
            'JWT (access token 15min + refresh token 7d)',
            'Mismos usuarios de tbpersona (nivelacceso: 1=básico, 2=supervisor, 3=admin)',
            'Tablas nuevas: tbrefresh_tokens, tbaccesos_api',
          ],
          decision: 'Misma base de usuarios, diferente mecanismo de sesión. No hay conflicto.',
        },
      ],

      seccionesBd: [
        {
          titulo: 'Tablas principales — Ingresos (compras)',
          items: [
            { nombre: 'tbregistro_doc_ingreso', desc: 'Cabecera de cada compra/factura. actualizastock=false=pendiente, true=stock actualizado.' },
            { nombre: 'tbdetalle_reg_ing', desc: 'Líneas de la compra (1 fila por talla/presentación). Campos nuevos: cantidad_recibida, barcode_recepcion, stock_actualizado.' },
            { nombre: 'tbregistro_doc_guia_ingreso', desc: 'Guías de remisión previas a la factura (sistema del ingeniero).', nota: 'Solo lectura' },
          ],
        },
        {
          titulo: 'Tablas principales — Ventas',
          items: [
            { nombre: 'tbregistro_doc_pedido', desc: 'Cabecera de ventas. Fuente de verdad de todas las ventas.' },
            { nombre: 'tbdetalle_reg_pedido', desc: 'Líneas de venta.' },
            { nombre: 'tbctas_cobrar', desc: 'Cuentas por cobrar (crédito a clientes).' },
          ],
        },
        {
          titulo: 'Tablas principales — Productos',
          items: [
            { nombre: 'tbproducto', desc: '1 fila por talla. Campos: idlinea, idgrupo, idtalla (género), codigo_prov, usa (talla USA).' },
            { nombre: 'tbpresentacion', desc: 'Unidad de medida del producto. Tiene codbarras y precvta. idpres es la clave para todo.' },
            { nombre: 'tbstockactualalmacen', desc: 'Stock actual por almacén. Actualizado por triggers al insertar en tbdetalle_reg_ing.' },
          ],
        },
        {
          titulo: 'Tablas de kardex e inventario',
          items: [
            { nombre: 'tbkardexingreso', desc: 'Movimientos de entrada. Lo escribe nuestro sistema al registrar una compra con actualizastock=true.' },
            { nombre: 'inventarios.inventario', desc: 'Kardex histórico reconstruido por el batch del ingeniero (generarmovimientos_inventario).', nota: 'Batch' },
            { nombre: 'tbreporte_kardex_valorizado_detalle', desc: 'Tabla de reporte pre-calculada. La lee el modal de kardex en el web.', nota: 'Batch' },
          ],
        },
        {
          titulo: 'Tablas solo del sistema web (nuevas)',
          items: [
            { nombre: 'tbrefresh_tokens', desc: 'JWT refresh tokens. Creada por migrations/001_roles_usuarios.sql.' },
            { nombre: 'tbaccesos_api', desc: 'Log de logins al sistema web.' },
            { nombre: 'tbventas_anuladas', desc: 'Vacía, evita errores en queries de anulaciones.' },
          ],
        },
        {
          titulo: 'Reglas de negocio clave en la BD',
          items: [
            { nombre: 'actualizastock = false', desc: 'Compra importada por XML → pendiente de recepción física. Stock NO actualizado.' },
            { nombre: 'actualizastock = true', desc: 'Compra registrada manualmente o cerrada → stock actualizado, kardex registrado.' },
            { nombre: 'precvta = 0', desc: 'Producto creado al importar XML, sin precio de venta asignado todavía.' },
            { nombre: 'stock_actualizado (por fila)', desc: 'En recepción parcial: marca las tallas que ya entraron al stock, aunque la compra siga abierta.' },
          ],
        },
      ],
    };
  },

  watch: {
    tabActivo(val) {
      if (val === 'mer') this.$nextTick(() => this.calcularMer());
    },
  },

  computed: {
    resumen() {
      const bugs = this.modulos.reduce((a, m) => a + m.bugs.length, 0);
      const pendientes = this.modulos.reduce((a, m) => a + m.pendientes.length, 0);
      const criticos = this.modulos.reduce((a, m) => a + m.pendientes.filter(p => p.startsWith('🔴')).length, 0);
      const funcionales = this.modulos.filter(m => m.estado === 'Funcional').length;
      return [
        { label: 'Módulos funcionales', valor: `${funcionales}/${this.modulos.length}`, color: 'text-success' },
        { label: 'Bugs conocidos',       valor: bugs,       color: bugs > 0 ? 'text-danger' : 'text-success' },
        { label: 'Pendientes críticos',  valor: criticos,   color: criticos > 0 ? 'text-danger' : 'text-success' },
        { label: 'Total pendientes',     valor: pendientes, color: 'text-warning' },
      ];
    },
  },

  methods: {
    estadoBadge(estado) {
      if (estado === 'Funcional')     return 'bg-success';
      if (estado === 'Básico')        return 'bg-secondary';
      if (estado === 'En desarrollo') return 'bg-warning text-dark';
      return 'bg-secondary';
    },
    headerBgEstado(estado) {
      if (estado === 'Funcional')     return '#f0fdf4';
      if (estado === 'En desarrollo') return '#fffbeb';
      return '#f8f9fa';
    },

    calcularMer() {
      const cont = this.$refs.merContenedor;
      if (!cont) return;

      const getPos = (id) => {
        const el = cont.querySelector(`[data-mer-id="${id}"]`);
        if (!el) return null;
        let left = 0, top = 0, curr = el;
        while (curr && curr !== cont) { left += curr.offsetLeft; top += curr.offsetTop; curr = curr.offsetParent; }
        return { x: left, y: top, w: el.offsetWidth, h: el.offsetHeight };
      };

      const link = (fromId, toId, color, label) => {
        const f = getPos(fromId), t = getPos(toId);
        if (!f || !t) return null;
        let x1, y1, x2, y2;
        if (f.x + f.w + 10 < t.x) {
          x1 = f.x + f.w; y1 = f.y + f.h / 2; x2 = t.x; y2 = t.y + t.h / 2;
        } else if (f.x > t.x + t.w + 10) {
          x1 = f.x; y1 = f.y + f.h / 2; x2 = t.x + t.w; y2 = t.y + t.h / 2;
        } else if (f.y + f.h < t.y) {
          x1 = f.x + f.w / 2; y1 = f.y + f.h; x2 = t.x + t.w / 2; y2 = t.y;
        } else {
          x1 = f.x + f.w / 2; y1 = f.y; x2 = t.x + t.w / 2; y2 = t.y + t.h;
        }
        const dx = Math.max(Math.abs(x2 - x1) * 0.45, 30);
        const dy = Math.max(Math.abs(y2 - y1) * 0.45, 30);
        let cx1, cy1, cx2, cy2;
        if (Math.abs(x2 - x1) > Math.abs(y2 - y1)) {
          cx1 = x1 + (x2 > x1 ? dx : -dx); cy1 = y1;
          cx2 = x2 - (x2 > x1 ? dx : -dx); cy2 = y2;
        } else {
          cx1 = x1; cy1 = y1 + (y2 > y1 ? dy : -dy);
          cx2 = x2; cy2 = y2 - (y2 > y1 ? dy : -dy);
        }
        return {
          id: `${fromId}-${toId}`,
          d: `M${x1},${y1} C${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`,
          mx: (x1 + x2) / 2,
          my: (y1 + y2) / 2,
          color, label,
        };
      };

      const rels = [
        link('tbpresentacion',             'tbproducto',                   '#16a34a', 'idprod 1:1'),
        link('tbregistro_doc_ingreso',     'tbpersona',                    '#1d4ed8', 'proveedor'),
        link('tbregistro_doc_ingreso',     'tbalmacen',                    '#475569', 'sede'),
        link('tbdetalle_reg_ing',          'tbregistro_doc_ingreso',       '#c2410c', 'cabecera'),
        link('tbdetalle_reg_ing',          'tbpresentacion',               '#16a34a', 'talla'),
        link('tbregistro_doc_pedido',      'tbpersona',                    '#1d4ed8', 'cliente'),
        link('tbregistro_doc_pedido',      'tbusuario',                    '#475569', 'vendedor'),
        link('tbregistro_doc_pedido',      'tbalmacen',                    '#475569', 'sede'),
        link('tbdetalle_reg_ped',          'tbregistro_doc_pedido',        '#86198f', 'cabecera'),
        link('tbdetalle_reg_ped',          'tbpresentacion',               '#16a34a', 'talla'),
        link('tbctas_cobrar',              'tbregistro_doc_pedido',        '#86198f', 'pedido'),
        link('tbusuario',                  'tbpersona',                    '#1d4ed8', 'es persona'),
        link('tbregistrotransferencia_salida', 'tbalmacen',               '#5b21b6', 'origen/destino'),
        link('tbdetalletransferencia_salida', 'tbregistrotransferencia_salida', '#5b21b6', 'cabecera'),
        link('tbdetalletransferencia_salida', 'tbpresentacion',            '#16a34a', 'talla'),
        link('tbinventarioparcialdetallesprod', 'tbinventarioparcialcab', '#854d0e', 'conteo'),
        link('tbinventarioparcialdetallesprod', 'tbpresentacion',          '#16a34a', 'talla'),
      ];

      this.merRelaciones = rels.filter(Boolean);
      this.merSvgW = cont.scrollWidth;
      this.merSvgH = cont.scrollHeight;
    },
  },
};
</script>

<style scoped>
.mer-domain {
  border: 1.5px dashed;
  border-radius: 8px;
  padding: 8px;
}
.mer-domain-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 6px;
}
.mer-table {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 5px;
  padding: 6px 8px;
  margin-bottom: 6px;
  font-size: 11px;
}
.mer-table.mer-hub {
  border-width: 2.5px;
}
.mer-table-name {
  font-weight: 700;
  font-size: 11px;
  margin-bottom: 2px;
}
.mer-rows {
  font-weight: 400;
  font-size: 9px;
  color: #9ca3af;
  margin-left: 3px;
}
.mer-hr {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 3px 0;
}
.mer-field {
  font-size: 10px;
  color: #374151;
  line-height: 1.6;
}
.mer-note {
  font-size: 9px;
  color: #9ca3af;
  font-style: italic;
  margin-top: 1px;
  margin-bottom: 2px;
}
.mer-pk {
  display: inline-block;
  background: #fef3c7;
  color: #92400e;
  font-size: 9px;
  font-weight: 700;
  padding: 0 3px;
  border-radius: 2px;
  margin-right: 2px;
}
.mer-fk {
  display: inline-block;
  background: #ede9fe;
  color: #4c1d95;
  font-size: 9px;
  font-weight: 700;
  padding: 0 3px;
  border-radius: 2px;
  margin-right: 2px;
}
.mer-chip {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid currentColor;
  opacity: 0.8;
}
</style>
